import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { Box, CircularProgress, IconButton, TextField, Typography } from "@mui/material";
import { Check, X } from "lucide-react";
const PINK = "#EE2A7B";
const ERROR = "#D92D20";
const ErrorText = ({ text }) => (_jsx(Typography, { sx: {
        position: "absolute",
        top: "100%",
        left: 0,
        mt: "2px",
        fontSize: "11px !important",
        color: ERROR,
        whiteSpace: "nowrap",
        lineHeight: 1.2,
    }, children: text }));
const ReportNameEditor = ({ value, onChange, disabled = false, prefix, error }) => {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value);
    const [saving, setSaving] = useState(false);
    const [localError, setLocalError] = useState("");
    // A rename from elsewhere (record load, clone, auto name) has to reach the
    // field while it is closed; an open field keeps whatever is being typed.
    useEffect(() => {
        if (!editing)
            setDraft(value);
    }, [value, editing]);
    const startEditing = () => {
        if (disabled)
            return;
        setDraft(value);
        setLocalError("");
        setEditing(true);
    };
    const save = async () => {
        if (saving)
            return;
        const trimmed = draft.trim();
        if (!trimmed) {
            setLocalError("Report name is required.");
            return;
        }
        if (trimmed === value) {
            setEditing(false);
            return;
        }
        setSaving(true);
        try {
            const problem = await onChange(trimmed);
            if (typeof problem === "string" && problem) {
                setLocalError(problem);
                return;
            }
            setLocalError("");
            setEditing(false);
        }
        finally {
            setSaving(false);
        }
    };
    const cancel = () => {
        if (saving)
            return;
        setDraft(value);
        setLocalError("");
        setEditing(false);
    };
    const shownError = editing ? localError || error : error;
    if (editing) {
        return (_jsxs(Box, { sx: { position: "relative", display: "flex", alignItems: "center", gap: 0.5 }, children: [_jsx(TextField, { value: draft, onChange: (e) => {
                        setDraft(e.target.value);
                        setLocalError("");
                    }, onKeyDown: (e) => {
                        if (e.key === "Enter")
                            save();
                        if (e.key === "Escape")
                            cancel();
                    }, autoFocus: true, size: "small", error: Boolean(shownError), sx: {
                        minWidth: 260,
                        "& .MuiOutlinedInput-root": {
                            height: "28px",
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#101828",
                            "& fieldset": { borderColor: shownError ? ERROR : PINK, borderRadius: "6px" },
                            "&:hover fieldset": { borderColor: shownError ? ERROR : PINK },
                            "&.Mui-focused fieldset": {
                                borderColor: shownError ? ERROR : PINK,
                                boxShadow: "0px 0px 0px 3px rgba(238,42,123,0.1)",
                            },
                        },
                    } }), _jsx("span", { children: _jsx(IconButton, { size: "small", "aria-label": "Save report name", onClick: save, disabled: saving, sx: {
                            width: 28,
                            height: 28,
                            borderRadius: "6px",
                            bgcolor: PINK,
                            color: "#FFFFFF",
                            "&:hover": { bgcolor: "#D61F6C" },
                            "&.Mui-disabled": { bgcolor: "#F9A8D4", color: "#FFFFFF" },
                        }, children: saving ? _jsx(CircularProgress, { size: 14, color: "inherit" }) : _jsx(Check, { size: 16 }) }) }), _jsx("span", { children: _jsx(IconButton, { size: "small", "aria-label": "Cancel renaming", onClick: cancel, disabled: saving, sx: {
                            width: 28,
                            height: 28,
                            borderRadius: "6px",
                            border: "1px solid #D0D5DD",
                            color: "#344054",
                            "&:hover": { bgcolor: "#F9FAFB" },
                        }, children: _jsx(X, { size: 16 }) }) }), shownError && _jsx(ErrorText, { text: shownError })] }));
    }
    return (_jsxs(Box, { sx: { position: "relative", display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }, children: [prefix && (_jsxs(_Fragment, { children: [_jsx(Typography, { sx: { fontSize: "14px", fontWeight: 600, color: "#667085", whiteSpace: "nowrap" }, children: prefix }), _jsx(Typography, { sx: { fontSize: "14px", color: "#D0D5DD" }, children: "\u203A" })] })), _jsx(Typography, { onClick: startEditing, title: value, sx: {
                    fontSize: "14px",
                    fontWeight: 600,
                    color: shownError ? ERROR : "#101828",
                    cursor: disabled ? "default" : "pointer",
                    maxWidth: 320,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    borderBottom: "1px dashed transparent",
                    ...(disabled
                        ? {}
                        : { "&:hover": { borderBottomColor: PINK, color: PINK } }),
                }, children: value }), !disabled && (
            // <HoverTooltip title="Edit report name" placement="top" useOverlay={true} disableInteractive>
            _jsx(IconButton, { size: "small", "aria-label": "Edit report name", onClick: startEditing, sx: { p: "2px", color: "#98A2B3", "&:hover": { color: PINK } }, children: _jsxs("svg", { width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [_jsx("path", { d: "M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" }), _jsx("path", { d: "M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" })] }) })
            // </HoverTooltip>
            ), shownError && _jsx(ErrorText, { text: shownError })] }));
};
export default ReportNameEditor;
